import { useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import { esteUrlPdf } from '@/shared/utils/este-url-pdf';
import type { LessonAnswerReportDto } from '../../../../api/generated/types.gen';
import { api } from '../../../../core/api/queries';
import { lessonsAPI } from '../../api';
import { useReportPage } from '../../../../shared/hooks';
import { ReportPageLayout } from '../../../../shared/components/layouts';
import { EditionSelector } from '../../../../shared/components/tables';
import { useLessonAnswersMutations } from '../../hooks/use-lesson-answers-mutations';
import { columnBuilder } from '../../../../shared/utils';
import { ActionButton } from '../../../../shared/components/buttons/ActionButton';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../../components/ui/dialog';

// Domain components
import { ApprovalDialog } from '../../components/ApprovalDialog';
import { AnswerDetailsDialog } from './components/AnswerDetailsDialog';
import { RejectionDialog } from '../../../../shared/components/dialogs/RejectionDialog';

// Utils & Config
import { formatUnixTimestamp } from '../../../../shared/utils';
import { lessonTypeConfigs } from '../../config/lesson-types.config';
import { useAuthProviderV2, useRoutesProvider } from '../../../../providers/provider-hooks';

/**
 * Lesson Answers Report Page
 *
 * Displays paginated, filterable report of student answers to lessons, quizzes, and challenges.
 * Features approval workflow for ADMIN and PROFESOR roles.
 *
 * Features:
 * - 15+ columns (role-based: admin sees school info)
 * - 3 dialogs (ApprovalDialog, AnswerDetailsDialog, DeleteDialog)
 * - Lesson type configuration logic (requiresApproval)
 * - Complex role permissions (PROFESOR restrictions for certain lesson types)
 * - Async lesson fetching (loads full lesson in details dialog)
 *
 * Architecture (Refactored 2025-11-01):
 * - Uses useReportPage hook (consolidates useState, useQuery, useSpringReport, useReportExport)
 * - Uses ReportPageLayout component (consolidates JSX structure)
 * - Uses columnBuilder for column definitions
 * - Page-specific: 3 dialogs, mutations, lesson type config, async lesson loading
 * - Reduced from 509 lines to ~455 lines (11% reduction)
 */
export function LessonAnswersReportPage() {
    // ==========================================
    // 1. DATA FETCHING & REPORT INTERFACE
    // ==========================================

    const { report, adapters, handleExport, editions, currentEdition, changeEdition } = useReportPage<LessonAnswerReportDto>({
        queryHook: api.lessons.answers.report,
        initialSort: [],
        exportEndpoint: '/api/lessons/answers/report',
        exportFilename: 'raport_raspunsuri_lectii',
        withEditions: true  // Enable edition filtering for ADMIN users
    });

    // ==========================================
    // 2. PAGE-SPECIFIC HOOKS
    // ==========================================

    const mutations = useLessonAnswersMutations();

    // ==========================================
    // 3. AUTH & ROLES
    // ==========================================

    const { userInfo } = useAuthProviderV2();
    const { ROLES } = useRoutesProvider();

    const isAdmin = [ROLES.ADMIN, ROLES.DBEST].includes(userInfo?.role);
    const isProfesor = userInfo?.role === ROLES.PROFESOR;
    const canApprove = isAdmin || isProfesor;

    // ==========================================
    // 4. PAGE-SPECIFIC STATE (DIALOGS)
    // ==========================================

    const [selectedAnswer, setSelectedAnswer] = useState<LessonAnswerReportDto | null>(null);
    const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
    const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false);
    const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    // Details dialog state (for lesson data)
    const [lessonData, setLessonData] = useState<any | null>(null);

    // ==========================================
    // 5. ACTION HANDLERS
    // ==========================================

    const handleOpenApprovalDialog = (answer: LessonAnswerReportDto) => {
        setSelectedAnswer(answer);
        setIsApprovalDialogOpen(true);
    };

    const handleConfirmApproval = async (value: { points?: number; fise?: number }) => {
        if (!selectedAnswer?.id) return;
        await mutations.approve.mutateAsync({
            id: selectedAnswer.id,
            ...value,
        });
    };

    const handleOpenRejectDialog = (answer: LessonAnswerReportDto) => {
        setSelectedAnswer(answer);
        setIsRejectionDialogOpen(true);
    };

    const handleConfirmReject = async (reason: string) => {
        if (!selectedAnswer?.id) return;
        await mutations.reject.mutateAsync({
            id: selectedAnswer.id,
            reason: reason || 'Răspuns respins',
        });
    };

    const handleOpenDetailsDialog = async (answer: LessonAnswerReportDto) => {
        setSelectedAnswer(answer);
        setLessonData(answer.lesson || null);
        setIsDetailsDialogOpen(true);

        // Fetch full lesson data if available
        if (answer.lesson?.id) {
            try {
                const fullLesson = await lessonsAPI.getById(answer.lesson.id);
                setLessonData(fullLesson);
            } catch (error) {
                console.error('Failed to fetch full lesson data:', error);
            }
        }
    };

    const handleOpenDeleteDialog = (answer: LessonAnswerReportDto) => {
        setSelectedAnswer(answer);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedAnswer) return;

        const userId = selectedAnswer.user?.id;
        const lessonId = selectedAnswer.lesson?.id;
        const editionId = selectedAnswer.editionId || userInfo?.editionId;

        if (!userId || !lessonId || !editionId) return;

        await mutations.deleteAnswer.mutateAsync({ userId, lessonId, editionId });
        setIsDeleteDialogOpen(false);
        setSelectedAnswer(null);
    };

    // ==========================================
    // 6. COLUMN DEFINITIONS (using builder)
    // ==========================================

    const columns = useMemo(
        () =>
            columnBuilder<LessonAnswerReportDto>()
                // ADMIN & PROFESOR: Actions column
                .when(canApprove, (b) =>
                    b.action('actions', 'Acțiuni', (_, row) => {
                        const typeAlias = row.lesson?.type;
                        const config = typeAlias
                            ? lessonTypeConfigs[typeAlias as keyof typeof lessonTypeConfigs]
                            : null;
                        const requiresApproval = config?.features.requiresApproval ?? false;
                        const isPendingApproval = row.state === 'PENDING_APPROVAL';

                        if (!requiresApproval || !isPendingApproval) {
                            return <span className="text-muted-foreground text-xs">N/A</span>;
                        }

                        // PROFESOR cannot approve any lesson type (only ADMIN/DBEST can approve)
                        if (isProfesor) {
                            return <span className="text-muted-foreground text-xs">N/A</span>;
                        }

                        return (
                            <div className="flex gap-2">
                                <ActionButton
                                    intent="approve"
                                    size="sm"
                                    onClick={() => handleOpenApprovalDialog(row)}
                                    disabled={mutations.isApproving}
                                    isLoading={mutations.isApproving}
                                >
                                    Aprobă
                                </ActionButton>
                                <ActionButton
                                    intent="reject"
                                    size="sm"
                                    onClick={() => handleOpenRejectDialog(row)}
                                    disabled={mutations.isRejecting}
                                    isLoading={mutations.isRejecting}
                                >
                                    Respinge
                                </ActionButton>
                            </div>
                        );
                    })
                )
                // ADMIN: Delete column
                .when(isAdmin, (b) =>
                    b.action('delete', 'Șterge', (_, row) => {
                        const userId = row.user?.id;
                        const lessonId = row.lesson?.id;
                        const editionId = row.editionId || userInfo?.editionId;

                        if (!userId || !lessonId || !editionId) {
                            return <span className="text-muted-foreground text-xs">N/A</span>;
                        }

                        return (
                            <ActionButton
                                intent="ghost"
                                size="sm"
                                onClick={() => handleOpenDeleteDialog(row)}
                                disabled={mutations.isDeleting || report.loading}
                                className="text-error hover:text-error/80"
                            >
                                Șterge
                            </ActionButton>
                        );
                    })
                )
                // Content column (challenges only)
                .action('content', 'Conținut', (_, row) => {
                    const typeAlias = row.lesson?.type;
                    const isChallenge =
                        typeAlias === 'MONTHLY_CHALLENGE' ||
                        typeAlias === 'DAILY_CHALLENGE' ||
                        typeAlias === 'PRACTICAL_ACTIVITY' ||
                        typeAlias === 'SCHOOL_CHALLENGE' ||
                        typeAlias === 'CLASS_EXERCISE';

                    if (!isChallenge) {
                        return <span className="text-muted-foreground text-xs">N/A</span>;
                    }

                    return (
                        <ActionButton
                            intent="approve"
                            size="sm"
                            onClick={() => handleOpenDetailsDialog(row)}
                            disabled={report.loading}
                        >
                            Afișează
                        </ActionButton>
                    );
                })
                // Status column (custom render)
                .custom({
                    key: 'state',
                    label: 'Status',
                    sortable: true,
                    filterable: true,
                    filterType: 'text',
                    filterPlaceholder: 'Status...',
                    render: (value) => {
                        switch (value) {
                            case 'PENDING_APPROVAL':
                                return (
                                    <span className="text-warning font-medium">
                                        În așteptare acceptare admin
                                    </span>
                                );
                            case 'APPROVED':
                                return <span className="text-success font-medium">✓ Acceptat</span>;
                            case 'REJECTED':
                                return (
                                    <span className="text-destructive font-medium">Respins</span>
                                );
                            case 'COMPLETED':
                                return <span className="text-success font-medium">Completat</span>;
                            case 'IN_PROGRESS':
                                return (
                                    <span className="text-muted-foreground font-medium">
                                        În curs
                                    </span>
                                );
                            default:
                                return <span className="text-muted-foreground">-</span>;
                        }
                    },
                })
                // B3: miniatura primei poze din depunere (`primaPoza`/`numarPoze`, calculate de backend la
                // citire). `action` = coloana nesortabila si nefiltrabila. Tap pe miniatura = acelasi dialog
                // ca „Afișează"; butonul are 48px, deci trece de tinta de 44px si pe telefon.
                .action('dovezi', 'Dovezi', (_, row) => {
                    if (!row.primaPoza) return <span className="text-muted-foreground">—</span>;
                    const titlu = row.lesson?.title ?? 'activitate';
                    const rest = (row.numarPoze ?? 1) - 1;
                    // Task 15: prima dovada poate fi PDF — tile cu icon, nu `<img>` spart.
                    const ePdf = esteUrlPdf(row.primaPoza);
                    return (
                        <button
                            type="button"
                            aria-label={
                                rest > 0
                                    ? `Vezi dovezile pentru ${titlu} (${rest + 1} fișiere)`
                                    : `Vezi dovezile pentru ${titlu}`
                            }
                            onClick={() => handleOpenDetailsDialog(row)}
                            className="relative inline-flex size-12 shrink-0 rounded focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                        >
                            {ePdf ? (
                                // Eticheta sus, iconul sub ea: badge-ul „+N" din coltul de jos nu le acopera.
                                <span className="flex size-12 flex-col items-center justify-start gap-0.5 rounded border border-border bg-muted pt-1 text-magenta">
                                    <span className="text-xs font-semibold leading-none">PDF</span>
                                    <FileText className="size-5" aria-hidden="true" />
                                </span>
                            ) : (
                            <img
                                src={row.primaPoza}
                                width={48}
                                height={48}
                                loading="lazy"
                                alt={`Dovadă pentru ${titlu}`}
                                className="size-12 rounded object-cover"
                            />
                            )}
                            {rest > 0 && (
                                <Badge className="absolute -bottom-1 -right-1 px-1.5 tabular-nums" aria-hidden="true">
                                    +{rest}
                                </Badge>
                            )}
                        </button>
                    );
                })
                // Cine a depus (colaborator anonim, cu numele tastat liber, sau elevul/profesorul contului)
                .custom({
                    key: 'submittedByName',
                    label: 'Depus de',
                    sortable: true,
                    filterable: true,
                    filterType: 'text',
                    filterPlaceholder: 'Depus de...',
                    render: (_, row) =>
                        row.collaboratorLink
                            ? `${row.submittedByName ?? '—'} (${row.collaboratorLink.label})`
                            : row.user?.nume ?? '—',
                })
                // Reject reason
                .text('rejectReason', 'Motiv respingere', 'Motiv...')
                // Timestamps
                .timestamp('startedAt', 'Data începere completare')
                .timestamp('timestamp', 'Data finalizare completare')
                // Lesson info
                .custom({
                    key: 'lesson.type',
                    label: 'Tip activitate',
                    sortable: true,
                    filterable: true,
                    filterType: 'text',
                    filterPlaceholder: 'Tip...',
                    render: (_, row) => {
                        const typeAlias = row.lesson?.type;
                        const config = typeAlias
                            ? lessonTypeConfigs[typeAlias as keyof typeof lessonTypeConfigs]
                            : null;
                        return config?.displayName || typeAlias || '-';
                    },
                })
                .nested('lesson.title', 'Titlu activitate', (row) => row.lesson?.title)
                .custom({
                    key: 'lesson.deadline',
                    label: 'Termen limită',
                    sortable: true,
                    filterable: false,
                    render: (_, row) => {
                        if (!row.lesson?.deadline) return '-';
                        const deadline =
                            row.lesson.deadline instanceof Date
                                ? row.lesson.deadline
                                : new Date(row.lesson.deadline as any);
                        const isExpired = deadline.getTime() < Date.now();
                        return (
                            <span
                                className={
                                    isExpired ? 'text-destructive font-medium' : 'text-foreground'
                                }
                            >
                                {formatUnixTimestamp(deadline)}
                                {isExpired && ' (expirat)'}
                            </span>
                        );
                    },
                })
                // User info
                // Titlul randului in rezumatul de pe mobil. Fara el, `coloanaTitlu`
                // cadea pe prima coloana de date, iar cele trei carduri „Primele 3"
                // aratau DOAR numarul de ordine — masurat: 3 din 10 carduri fara
                // niciun text, la 360.
                .nested('user.nume', 'Nume utilizator', (row) => row.user?.nume, {
                    cardRole: 'title'
                })
                .nested('user.email', 'Email utilizator', (row) => row.user?.email)
                // ADMIN: School info columns
                .when(isAdmin, (b) =>
                    b
                        .nested(
                            'user.school.numeInstitutieInvatamant',
                            'Școala',
                            (row) => row.user?.school?.numeInstitutieInvatamant
                        )
                        .nested(
                            'user.school.codSe',
                            'Cod SE',
                            (row) => row.user?.school?.codSe
                        )
                        .nested(
                            'user.school.adresa.judet',
                            'Județ',
                            (row) => row.user?.school?.adresa?.judet
                        )
                        .nested(
                            'user.school.adresa.oras',
                            'Oraș',
                            (row) => row.user?.school?.adresa?.oras
                        )
                )
                // Points (+ nota de plafon, ca badge, doar cand exista — vezi "Notă punctaj"
                // in raportul task-ului: coloana separata era goala pe marea majoritate a
                // randurilor, deci nota s-a mutat langa punctajul pe care il explica)
                .custom({
                    key: 'points',
                    label: 'Puncte',
                    sortable: true,
                    filterable: true,
                    filterType: 'number',
                    filterPlaceholder: 'Puncte...',
                    cardRole: 'value',
                    // ⚠️ NU `render`: acela intoarce si `Popover`-ul cu nota de
                    // plafon, adica un CONTROL, iar cardul din rezumatul mobil e
                    // pasiv (zero `onClick`/`role`/`tabIndex` in
                    // `ReportMobileSummary`) si isi asaza valoarea intr-un `span`
                    // de o linie. In card intra doar cifra; nota ramane in tabel,
                    // unde e loc pentru ea.
                    cardRender: (value) => (value !== undefined && value !== null ? value : '—'),
                    render: (value, row) => (
                        <div className="flex items-center gap-2">
                            <span>{value !== undefined && value !== null ? value : '-'}</span>
                            {row.scoringNote && (
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            aria-label={`Notă punctaj: ${row.scoringNote}`}
                                        >
                                            <Badge variant="secondary">Notă</Badge>
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent>
                                        <p className="text-medium">{row.scoringNote}</p>
                                    </PopoverContent>
                                </Popover>
                            )}
                        </div>
                    ),
                })
                .build(),
        [isAdmin, isProfesor, canApprove, mutations, report.loading, userInfo?.editionId]
    );

    // ==========================================
    // 7. RENDER
    // ==========================================

    return (
        <>
            <ReportPageLayout
                title="Raport Activități"
                report={report}
                adapters={adapters}
                handleExport={handleExport}
                columns={columns}
                showExport={isAdmin}
                extraControls={
                    isAdmin ? (
                        <EditionSelector
                            editions={editions || []}
                            currentEditionId={currentEdition?.id}
                            onChange={changeEdition || (() => {})}
                            disabled={report.loading}
                        />
                    ) : undefined
                }
            />

            {/* Dialogs */}
            <ApprovalDialog
                isOpen={isApprovalDialogOpen}
                onClose={() => {
                    setIsApprovalDialogOpen(false);
                    setSelectedAnswer(null);
                }}
                onApprove={handleConfirmApproval}
                lessonAnswer={selectedAnswer}
                loading={mutations.isApproving}
            />

            <RejectionDialog
                isOpen={isRejectionDialogOpen}
                onClose={() => {
                    setIsRejectionDialogOpen(false);
                    setSelectedAnswer(null);
                }}
                onReject={handleConfirmReject}
                loading={mutations.isRejecting}
                title="Respingere răspuns lecție"
                contextInfo={
                    selectedAnswer
                        ? [
                              {
                                  label: 'Elev',
                                  value: selectedAnswer.user?.nume || '-',
                              },
                              {
                                  label: 'Lecție',
                                  value: selectedAnswer.lesson?.title || '-',
                              },
                              {
                                  label: 'Data',
                                  value: selectedAnswer.timestamp
                                      ? new Date(selectedAnswer.timestamp).toLocaleString('ro-RO')
                                      : '-',
                              },
                          ]
                        : undefined
                }
                warningMessage="Răspunsul va fi marcat ca respins și elevul va fi notificat despre motiv."
            />

            <AnswerDetailsDialog
                open={isDetailsDialogOpen}
                onClose={() => {
                    setIsDetailsDialogOpen(false);
                    setSelectedAnswer(null);
                    setLessonData(null);
                }}
                answer={selectedAnswer}
                lesson={lessonData}
                userRole={userInfo?.role}
                onActionComplete={() => {
                    report.refetch();
                }}

            />

            <Dialog
                open={isDeleteDialogOpen}
                onOpenChange={() => {
                    setIsDeleteDialogOpen(false);
                    setSelectedAnswer(null);
                }}
            >
                <DialogContent className="alert-dialog-destructive">
                    <DialogHeader>
                        <DialogTitle>Confirmare ștergere răspunsuri</DialogTitle>
                        <DialogDescription>
                            Sigur doriți să ștergeți aceste răspunsuri? Această acțiune este
                            permanentă și nu poate fi anulată. Toate datele asociate (răspunsuri,
                            puncte, istoric) vor fi șterse definitiv.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            data-cancel
                            variant="outline"
                            onClick={() => {
                                setIsDeleteDialogOpen(false);
                                setSelectedAnswer(null);
                            }}
                            disabled={mutations.isDeleting}
                        >
                            Anulare
                        </Button>
                        <Button
                            data-action
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={mutations.isDeleting}
                        >
                            {mutations.isDeleting ? 'Se șterge...' : 'Șterge'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
